import * as THREE from 'three';
import type { Track } from '../track/TrackLoader';
import { trackSurfaceHeightAtSample } from '../track/TrackElevation';
import { DEFAULT_STYLE_CONFIG, type StyleConfig } from './StyleConfig';
import { installAerialPerspectiveForObject } from './AerialPerspective';
import { installValueCompositionForObject, tagValueBucketDeep } from './ValueComposition';
import type { ObstacleObb, SoftBarrier } from '../game/Collision';

const SHOULDER_GROUND_OFFSET = -0.12;
const INSTANCE_SCALE = new THREE.Vector3(1, 1, 1);
const Y_AXIS = new THREE.Vector3(0, 1, 0);

export interface TrackFurnitureOptions {
  style?: StyleConfig;
  useBatchedMesh?: boolean;
  /** Rail soft-barrier params; falls back to safe defaults if omitted. */
  rail?: { perpDamping: number; scrapeDrag: number; contactRadius: number };
  /** Fence soft-barrier params; lighter than rails by default. */
  fence?: { perpDamping: number; scrapeDrag: number; contactRadius: number };
}

// Rails/fences hold the racer like walls do (high perpDamping = inelastic),
// but the "soft" feel comes from scrape drag bleeding speed over contact
// time. A bouncy rail feels arcadey-wrong.
const DEFAULT_RAIL_BARRIER = { perpDamping: 0.85, scrapeDrag: 0.20, contactRadius: 0.35 };
const DEFAULT_FENCE_BARRIER = { perpDamping: 0.85, scrapeDrag: 0.12, contactRadius: 0.35 };

export interface TrackFurniture {
  readonly group: THREE.Group;
  /** Read-only at the start of each rebuild; safe to capture at construction time. */
  readonly collisionLines: SoftBarrier[];
  /** Stone-block walls along the boundary; treat as hard scenery obstacles. */
  readonly obstacles: ObstacleObb[];
  applyStyle(style: StyleConfig): void;
  update(dt: number): void;
  dispose(): void;
}

export function buildTrackFurniture(
  track: Track,
  options: TrackFurnitureOptions = {},
): TrackFurniture {
  const group = new THREE.Group();
  group.name = 'track-furniture';
  group.updateMatrix();
  group.matrixAutoUpdate = false;
  let style = options.style ?? DEFAULT_STYLE_CONFIG;
  let bannerTime = 0;
  const railParams = options.rail ?? DEFAULT_RAIL_BARRIER;
  const fenceParams = options.fence ?? DEFAULT_FENCE_BARRIER;
  const useBatchedMesh = options.useBatchedMesh ?? true;
  const collisionLines: SoftBarrier[] = [];
  const obstacles: ObstacleObb[] = [];
  // Cached during rebuild so `update()` doesn't have to traverse the entire
  // furniture subtree every frame. The traverse was the hottest item in
  // src/render/TrackFurniture.ts:update under PerfDiag.
  const bannerPlanes: THREE.Mesh[] = [];

  const rebuild = (): void => {
    disposeGroup(group);
    collisionLines.length = 0;
    obstacles.length = 0;
    bannerPlanes.length = 0;
    if (!style.furniture.enabled) return;
    if (style.furniture.railsEnabled) {
      group.add(buildRails(track, style, collisionLines, obstacles, railParams, fenceParams, useBatchedMesh));
    }
    if (style.furniture.chevronsEnabled) group.add(buildChevrons(track, style));
    if (style.furniture.bannersEnabled) group.add(buildBanners(track, style, obstacles));
    tagValueBucketDeep(group, 'mg');
    installAerialPerspectiveForObject(group);
    installValueCompositionForObject(group, 'mg');
    group.traverse((object) => {
      if (object instanceof THREE.Mesh && object.userData.bannerPlane) {
        bannerPlanes.push(object);
      }
    });
  };

  let lastSnapshot = rebuildSnapshot(style);
  rebuild();

  return {
    group,
    collisionLines,
    obstacles,
    applyStyle(nextStyle) {
      // applyStyle fires for every ScenePanel slider tweak — including
      // unrelated style fields like sky color or fog density. Tearing down
      // and rebuilding the entire rail / fence / wall / chevron / banner
      // mesh chain on each one was 2–5ms per change of pure waste. Diff
      // first; only rebuild when something the rebuild actually consumes
      // has changed.
      const nextSnapshot = rebuildSnapshot(nextStyle);
      style = nextStyle;
      if (nextSnapshot === lastSnapshot) return;
      lastSnapshot = nextSnapshot;
      rebuild();
    },
    update(dt) {
      bannerTime += dt;
      for (const banner of bannerPlanes) {
        banner.rotation.z = Math.sin(bannerTime * 1.7 + banner.userData.phase) * 0.035;
      }
    },
    dispose() {
      disposeGroup(group);
    },
  };
}

/**
 * Worst-case outboard distance the kerb mesh ever reaches, in metres past
 * the white-line edge. Mirrors the formula in `makeKerbStyle`
 * (`TrackRenderer.ts`): user-configured baseWidth + the segment-classifier
 * contribution (max legacy `peakWidth` × 0.35 ≈ 1.82) + the curvature-
 * driven `cornerWidthBoost`. All barrier furniture uses this so it stays
 * clear of the widest possible kerb when the user raises baseWidth or
 * cornerWidthBoost in the Style Lab.
 */
function maxKerbOuterWidth(style: StyleConfig): number {
  return style.kerbs.baseWidth + 1.82 + style.kerbs.cornerWidthBoost;
}

/**
 * Stable string of every style field that the furniture rebuild actually
 * consumes. Diffing on this lets `applyStyle` skip the full rebuild when
 * an unrelated style slider moves (sky, fog, lighting, etc.).
 */
function rebuildSnapshot(style: StyleConfig): string {
  const f = style.furniture;
  const k = style.kerbs;
  return [
    f.enabled, f.railsEnabled, f.chevronsEnabled, f.bannersEnabled,
    f.spacing, f.cornerDensity, f.bannerGlbUrl,
    k.baseWidth, k.cornerWidthBoost, k.redHex, k.whiteHex,
  ].join('|');
}

function furnitureGroundHeightAtSample(track: Track, centerlineIndex: number, signedLateral: number): number {
  return trackSurfaceHeightAtSample(track, centerlineIndex, signedLateral) + SHOULDER_GROUND_OFFSET;
}

function buildRails(
  track: Track,
  style: StyleConfig,
  collisionLines: SoftBarrier[],
  obstacles: ObstacleObb[],
  railParams: { perpDamping: number; scrapeDrag: number; contactRadius: number },
  fenceParams: { perpDamping: number; scrapeDrag: number; contactRadius: number },
  useBatchedMesh: boolean,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'track-side-rails';
  const guardrailRuns = new THREE.Group();
  guardrailRuns.name = 'guardrail-runs';
  const fenceRuns = new THREE.Group();
  fenceRuns.name = 'fence-runs';
  const wallRuns = new THREE.Group();
  wallRuns.name = 'wall-runs';

  for (const run of boundaryRuns(track)) {
    if (run.kind === 'rail') {
      guardrailRuns.add(buildGuardrailRun(track, style, run));
      collisionLines.push({ line: collisionLineFor(track, style, run, 2.06), ...railParams });
    } else if (run.kind === 'fence') {
      fenceRuns.add(buildFenceRun(track, style, run));
      collisionLines.push({ line: collisionLineFor(track, style, run, 2.28), ...fenceParams });
    } else {
      wallRuns.add(buildWallRun(track, style, run, obstacles));
      // Wall blocks are sparse (every 5 samples). Add a continuous polyline
      // behind them so the racer can't slip through gaps between blocks to
      // the (invisible) corridor wall.
      collisionLines.push({ line: collisionLineFor(track, style, run, 2.45), ...railParams });
    }
  }

  if (useBatchedMesh) {
    replaceStaticTubeMeshesWithBatch(guardrailRuns, 'guardrail-tube-batch');
    replaceStaticTubeMeshesWithBatch(fenceRuns, 'fence-cable-batch');
  }
  group.add(guardrailRuns, fenceRuns, wallRuns);
  return group;
}

/**
 * 2D centerline for a boundary run, projected onto the XZ plane at the same
 * lateral offset as the visual mesh. Sample stride matches `boundaryPoints`
 * so collision and visuals can never drift.
 */
function collisionLineFor(
  track: Track,
  style: StyleConfig,
  run: BoundaryRun,
  extraOffset: number,
): { x: number; z: number }[] {
  // Centerline points on the autodrome are ~6m apart. At max pod speed
  // (~95 m/s) the per-step travel is ~1.6 m, so we subdivide each centerline
  // segment into 1m-or-finer chunks. Without subdivision the swept polyline
  // test slips through at oblique angles between sample points.
  const MAX_SEGMENT_LEN = 0.8;
  const points: { x: number; z: number }[] = [];
  const projectSample = (i: number): { x: number; z: number } => {
    const sample = track.centerline[i % track.centerline.length];
    const { tx, tz } = track.tangents[i % track.centerline.length];
    const nx = -tz;
    const nz = tx;
    const edge = run.side > 0 ? sample.widthLeft : -sample.widthRight;
    const offset = edge + run.side * (maxKerbOuterWidth(style) + extraOffset);
    return { x: sample.x + nx * offset, z: sample.z + nz * offset };
  };
  for (let i = run.start; i <= run.end; i++) {
    const here = projectSample(i);
    if (i === run.start) {
      points.push(here);
      continue;
    }
    const prev = points[points.length - 1];
    const dx = here.x - prev.x;
    const dz = here.z - prev.z;
    const segLen = Math.hypot(dx, dz);
    if (segLen > MAX_SEGMENT_LEN) {
      const subdivs = Math.ceil(segLen / MAX_SEGMENT_LEN);
      for (let s = 1; s < subdivs; s++) {
        const t = s / subdivs;
        points.push({ x: prev.x + dx * t, z: prev.z + dz * t });
      }
    }
    points.push(here);
  }
  return points;
}

type BoundaryKind = 'rail' | 'fence' | 'wall';

interface BoundaryRun {
  kind: BoundaryKind;
  side: 1 | -1;
  start: number;
  end: number;
}

function boundaryRuns(track: Track): BoundaryRun[] {
  const n = track.centerline.length;
  const span = Math.max(28, Math.floor(n / 9));
  const kinds: BoundaryKind[] = ['rail', 'fence', 'rail', 'wall', 'fence', 'rail'];
  const runs: BoundaryRun[] = [];
  let cursor = 0;
  let ordinal = 0;
  // Continuous coverage — adjacent runs touch (cursor = end + 1) so collision
  // and visual barriers exist for every centerline sample. Earlier versions
  // left a 14-sample gap that produced an invisible corridor wall in the gap.
  while (cursor < n) {
    const end = Math.min(n - 1, cursor + span + (ordinal % 3) * 9);
    for (const side of [1, -1] as const) {
      const offset = side > 0 ? 0 : 2;
      runs.push({
        kind: kinds[(ordinal + offset) % kinds.length],
        side,
        start: cursor,
        end,
      });
    }
    cursor = end + 1;
    ordinal += 1;
  }
  return runs;
}

function buildGuardrailRun(track: Track, style: StyleConfig, run: BoundaryRun): THREE.Group {
  const group = new THREE.Group();
  group.name = `guardrail-run-${run.side > 0 ? 'left' : 'right'}-${run.start}`;
  const railMat = new THREE.MeshStandardMaterial({ color: 0x3b3f45, roughness: 0.44, metalness: 0.55 });
  const postMat = new THREE.MeshStandardMaterial({ color: 0x575b61, roughness: 0.55, metalness: 0.42 });
  const upper = boundaryPoints(track, style, run, 0.92, 2.1);
  const lower = boundaryPoints(track, style, run, 0.54, 2.0);
  group.add(makeTubeRun(`${group.name}-upper`, upper, 0.055, railMat));
  group.add(makeTubeRun(`${group.name}-lower`, lower, 0.05, railMat));
  addPosts(group, track, style, run, postMat, 1.2, 2.06, 0.085, 8);
  return group;
}

function buildFenceRun(track: Track, style: StyleConfig, run: BoundaryRun): THREE.Group {
  const group = new THREE.Group();
  group.name = `fence-run-${run.side > 0 ? 'left' : 'right'}-${run.start}`;
  const cableMat = new THREE.MeshStandardMaterial({ color: 0x24282d, roughness: 0.5, metalness: 0.5 });
  const postMat = new THREE.MeshStandardMaterial({ color: 0x555a61, roughness: 0.6, metalness: 0.36 });
  group.add(makeTubeRun(`${group.name}-top-cable`, boundaryPoints(track, style, run, 1.42, 2.35), 0.028, cableMat));
  group.add(makeTubeRun(`${group.name}-mid-cable`, boundaryPoints(track, style, run, 0.98, 2.3), 0.025, cableMat));
  group.add(makeTubeRun(`${group.name}-low-cable`, boundaryPoints(track, style, run, 0.58, 2.25), 0.022, cableMat));
  addPosts(group, track, style, run, postMat, 1.72, 2.28, 0.07, 7);
  return group;
}

function buildWallRun(
  track: Track,
  style: StyleConfig,
  run: BoundaryRun,
  obstacles: ObstacleObb[],
): THREE.Group {
  const group = new THREE.Group();
  group.name = `wall-run-${run.side > 0 ? 'left' : 'right'}-${run.start}`;
  const redMat = new THREE.MeshStandardMaterial({ color: style.kerbs.redHex, roughness: 0.62, metalness: 0.04 });
  const whiteMat = new THREE.MeshStandardMaterial({ color: style.kerbs.whiteHex, roughness: 0.64, metalness: 0.02 });
  const redBlocks: StaticInstance[] = [];
  const whiteBlocks: StaticInstance[] = [];
  const step = 5;
  for (let i = run.start; i <= run.end; i += step) {
    const sample = track.centerline[i % track.centerline.length];
    const { tx, tz } = track.tangents[i % track.centerline.length];
    const nx = -tz;
    const nz = tx;
    const edge = run.side > 0 ? sample.widthLeft : -sample.widthRight;
    const offset = edge + run.side * (maxKerbOuterWidth(style) + 2.45);
    const cx = sample.x + nx * offset;
    const cz = sample.z + nz * offset;
    const centerlineIndex = i % track.centerline.length;
    const position = new THREE.Vector3(
      cx,
      furnitureGroundHeightAtSample(track, centerlineIndex, offset) + 0.31,
      cz,
    );
    const instance = {
      matrix: composeInstanceMatrix(position, Math.atan2(tx, tz)),
      centerlineIndex,
      signedLateral: offset,
    };
    if (((i / step) % 2) < 1) redBlocks.push(instance);
    else whiteBlocks.push(instance);
    // Block geometry is BoxGeometry(2.85 across-tangent, 0.62 up, 0.45 along-normal).
    // For top-down OBB collision, "across" runs along the tangent and "along"
    // runs along the inward normal.
    obstacles.push({
      centerX: cx,
      centerZ: cz,
      axisAcrossX: tx,
      axisAcrossZ: tz,
      halfAcross: 1.425,
      halfAlong: 0.225,
    });
  }
  if (redBlocks.length > 0) {
    group.add(makeStaticInstancedMesh(
      `${group.name}-red-module`,
      new THREE.BoxGeometry(2.85, 0.62, 0.45),
      redMat,
      redBlocks,
      { castShadow: true, receiveShadow: true },
    ));
  }
  if (whiteBlocks.length > 0) {
    group.add(makeStaticInstancedMesh(
      `${group.name}-white-module`,
      new THREE.BoxGeometry(2.85, 0.62, 0.45),
      whiteMat,
      whiteBlocks,
      { castShadow: true, receiveShadow: true },
    ));
  }
  return group;
}

function boundaryPoints(
  track: Track,
  style: StyleConfig,
  run: BoundaryRun,
  height: number,
  extraOffset: number,
): THREE.Vector3[] {
  const points: THREE.Vector3[] = [];
  for (let i = run.start; i <= run.end; i += 3) {
    const sample = track.centerline[i % track.centerline.length];
    const { tx, tz } = track.tangents[i % track.centerline.length];
    const nx = -tz;
    const nz = tx;
    const edge = run.side > 0 ? sample.widthLeft : -sample.widthRight;
    const offset = edge + run.side * (maxKerbOuterWidth(style) + extraOffset);
    points.push(new THREE.Vector3(
      sample.x + nx * offset,
      furnitureGroundHeightAtSample(track, i % track.centerline.length, offset) + height,
      sample.z + nz * offset,
    ));
  }
  return points;
}

function makeTubeRun(name: string, points: THREE.Vector3[], radius: number, material: THREE.Material): THREE.Mesh {
  const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal');
  const mesh = new THREE.Mesh(new THREE.TubeGeometry(curve, Math.max(8, points.length * 4), radius, 6), material);
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function replaceStaticTubeMeshesWithBatch(root: THREE.Group, name: string): void {
  const items: Array<{ mesh: THREE.Mesh; parent: THREE.Object3D }> = [];
  root.traverse((object) => {
    if (object instanceof THREE.Mesh && object.geometry.type === 'TubeGeometry' && object.parent) {
      items.push({ mesh: object, parent: object.parent });
    }
  });
  if (items.length === 0) return;

  const firstMaterial = Array.isArray(items[0].mesh.material) ? items[0].mesh.material[0] : items[0].mesh.material;
  const material = firstMaterial.clone();
  const maxVertexCount = items.reduce(
    (sum, item) => sum + (item.mesh.geometry.getAttribute('position')?.count ?? 0),
    0,
  );
  const maxIndexCount = items.reduce(
    (sum, item) => sum + (item.mesh.geometry.getIndex()?.count ?? item.mesh.geometry.getAttribute('position')?.count ?? 0),
    0,
  );
  const batch = new THREE.BatchedMesh(items.length, maxVertexCount, maxIndexCount, material);
  batch.name = name;
  batch.castShadow = true;
  batch.receiveShadow = true;
  batch.perObjectFrustumCulled = true;
  batch.sortObjects = false;
  batch.updateMatrix();
  batch.matrixAutoUpdate = false;

  const disposedGeometries = new Set<THREE.BufferGeometry>();
  const disposedMaterials = new Set<THREE.Material>();
  for (const { mesh, parent } of items) {
    const geometryId = batch.addGeometry(mesh.geometry);
    const instanceId = batch.addInstance(geometryId);
    batch.setMatrixAt(instanceId, mesh.matrix);
    parent.remove(mesh);
    if (!disposedGeometries.has(mesh.geometry)) {
      disposedGeometries.add(mesh.geometry);
      mesh.geometry.dispose();
    }
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const sourceMaterial of materials) {
      if (disposedMaterials.has(sourceMaterial)) continue;
      disposedMaterials.add(sourceMaterial);
      sourceMaterial.dispose();
    }
  }
  batch.computeBoundingBox();
  batch.computeBoundingSphere();
  batch.userData.instanceCount = items.length;
  root.add(batch);
}

function addPosts(
  group: THREE.Group,
  track: Track,
  style: StyleConfig,
  run: BoundaryRun,
  material: THREE.Material,
  height: number,
  extraOffset: number,
  radius: number,
  step: number,
): void {
  const posts: StaticInstance[] = [];
  for (let i = run.start; i <= run.end; i += step) {
    const sample = track.centerline[i % track.centerline.length];
    const { tx, tz } = track.tangents[i % track.centerline.length];
    const nx = -tz;
    const nz = tx;
    const edge = run.side > 0 ? sample.widthLeft : -sample.widthRight;
    const offset = edge + run.side * (maxKerbOuterWidth(style) + extraOffset);
    const centerlineIndex = i % track.centerline.length;
    const position = new THREE.Vector3(
      sample.x + nx * offset,
      furnitureGroundHeightAtSample(track, centerlineIndex, offset) + height * 0.5,
      sample.z + nz * offset,
    );
    posts.push({
      matrix: composeInstanceMatrix(position, Math.atan2(tx, tz)),
      centerlineIndex,
      signedLateral: offset,
    });
  }
  if (posts.length === 0) return;
  group.add(makeStaticInstancedMesh(
    `${group.name}-posts`,
    new THREE.CylinderGeometry(radius, radius * 1.25, height, 7),
    material,
    posts,
    { castShadow: true },
  ));
}

function buildChevrons(track: Track, style: StyleConfig): THREE.Group {
  const group = new THREE.Group();
  group.name = 'chevron-warning-signs';
  const signs = findCurvaturePeaks(track, style);
  const signMat = makeChevronMaterial();
  const backingMat = new THREE.MeshStandardMaterial({ color: 0x15110d, roughness: 0.68, metalness: 0.08 });
  const postMat = new THREE.MeshStandardMaterial({ color: 0x2a211a, roughness: 0.65, metalness: 0.25 });
  const backingInstances: StaticInstance[] = [];
  const faceInstances: StaticInstance[] = [];
  const rearFaceInstances: StaticInstance[] = [];
  const postInstances: StaticInstance[] = [];

  for (const { index, sign } of signs) {
    const c = track.centerline[index];
    const { tx, tz } = track.tangents[index];
    const nx = -tz;
    const nz = tx;
    const edge = sign > 0 ? c.widthLeft : -c.widthRight;
    const lateral = edge + sign * (maxKerbOuterWidth(style) + 4.2);
    const x = c.x + nx * lateral;
    const z = c.z + nz * lateral;
    const baseY = furnitureGroundHeightAtSample(track, index, lateral);

    const holder = new THREE.Group();
    holder.name = 'chevron-sign';
    holder.userData.centerlineIndex = index;
    holder.userData.signedLateral = lateral;
    holder.position.set(x, baseY, z);
    holder.rotation.y = Math.atan2(tx, tz) + (sign > 0 ? -Math.PI * 0.5 : Math.PI * 0.5);

    const postHeight = 1.34;
    const holderMatrix = composeInstanceMatrix(holder.position, holder.rotation.y);
    const metadata = { centerlineIndex: index, signedLateral: lateral };
    backingInstances.push({
      matrix: composeChildInstanceMatrix(holderMatrix, new THREE.Vector3(0, 1.34, 0), 0),
      ...metadata,
    });
    faceInstances.push({
      matrix: composeChildInstanceMatrix(holderMatrix, new THREE.Vector3(0, 1.34, 0.076), 0),
      ...metadata,
    });
    rearFaceInstances.push({
      matrix: composeChildInstanceMatrix(holderMatrix, new THREE.Vector3(0, 1.34, -0.076), Math.PI),
      ...metadata,
    });
    for (const postX of [-1.32, 1.32]) {
      postInstances.push({
        matrix: composeChildInstanceMatrix(holderMatrix, new THREE.Vector3(postX, postHeight * 0.5, -0.03), 0),
        ...metadata,
      });
    }

    group.add(holder);
  }

  if (backingInstances.length > 0) {
    group.add(makeStaticInstancedMesh(
      'chevron-sign-backing',
      new THREE.BoxGeometry(3.72, 1.18, 0.14),
      backingMat,
      backingInstances,
      { castShadow: true, receiveShadow: true },
    ));
    group.add(makeStaticInstancedMesh(
      'chevron-sign-face',
      new THREE.PlaneGeometry(3.48, 1.0),
      signMat,
      faceInstances,
      { castShadow: true, receiveShadow: true },
    ));
    group.add(makeStaticInstancedMesh(
      'chevron-sign-rear-face',
      new THREE.PlaneGeometry(3.48, 1.0),
      signMat,
      rearFaceInstances,
      { castShadow: true, receiveShadow: true },
    ));
    group.add(makeStaticInstancedMesh(
      'chevron-sign-posts',
      new THREE.CylinderGeometry(0.055, 0.075, 1.34, 6),
      postMat,
      postInstances,
      { castShadow: true },
    ));
  }

  return group;
}

function buildBanners(track: Track, style: StyleConfig, obstacles: ObstacleObb[]): THREE.Group {
  const group = new THREE.Group();
  group.name = 'banner-poles';
  const poleMat = new THREE.MeshStandardMaterial({ color: 0x4b4741, roughness: 0.58, metalness: 0.35 });
  const crossMat = new THREE.MeshStandardMaterial({ color: 0x2b3036, roughness: 0.5, metalness: 0.55 });
  const bannerMats = [makeBannerMaterial('#cf222a', '#0d4f7a'), makeBannerMaterial('#1e5c90', '#d64b32')];
  const spacing = Math.max(24, style.furniture.spacing);
  let nextArc = 0;
  let bannerIndex = 0;

  for (let i = 0; i < track.centerline.length; i++) {
    const arc = track.cumulativeLength[i];
    if (arc < nextArc) continue;
    nextArc = arc + spacing;
    const side = bannerIndex % 2 === 0 ? 1 : -1;
    const c = track.centerline[i];
    const { tx, tz } = track.tangents[i];
    const nx = -tz;
    const nz = tx;
    const edge = side > 0 ? c.widthLeft : -c.widthRight;
    const offset = edge + side * (maxKerbOuterWidth(style) + 8.5);
    const baseY = furnitureGroundHeightAtSample(track, i, offset);
    const cx = c.x + nx * offset;
    const cz = c.z + nz * offset;

    const holder = new THREE.Group();
    holder.name = 'banner-pole';
    holder.position.set(cx, baseY, cz);
    holder.rotation.y = Math.atan2(tx, tz);

    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.14, 7.2, 7), poleMat);
    pole.position.y = 3.6;
    pole.castShadow = true;
    const cross = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.12, 0.12), crossMat);
    cross.position.set(side * -0.9, 6.8, 0);
    cross.castShadow = true;
    const banner = new THREE.Mesh(new THREE.PlaneGeometry(1.45, 3.2, 4, 8), bannerMats[bannerIndex % bannerMats.length]);
    banner.position.set(side * -0.9, 5.0, 0.04);
    banner.userData.bannerPlane = true;
    banner.userData.phase = bannerIndex * 0.73;
    banner.castShadow = true;
    holder.add(pole, cross, banner);
    group.add(holder);

    // The pole is a thin cylinder (radius ~0.14m, height 7.2m). Top-down
    // collision only needs the footprint; use a small square OBB aligned
    // with the track tangent so the across/along axes match the visible
    // pole orientation.
    obstacles.push({
      centerX: cx,
      centerZ: cz,
      axisAcrossX: tx,
      axisAcrossZ: tz,
      halfAcross: 0.2,
      halfAlong: 0.2,
    });

    bannerIndex += 1;
  }

  group.userData.bannerGlbUrl = style.furniture.bannerGlbUrl;
  return group;
}

function findCurvaturePeaks(track: Track, style: StyleConfig): Array<{ index: number; sign: 1 | -1 }> {
  const out: Array<{ index: number; sign: 1 | -1 }> = [];
  const minGap = Math.max(8, Math.round(18 - style.furniture.cornerDensity * 10));
  let last = -minGap;
  for (let i = 0; i < track.centerline.length; i++) {
    const curvature = curvatureAt(track, i);
    if (curvature < THREE.MathUtils.lerp(0.72, 0.24, style.furniture.cornerDensity)) continue;
    if (i - last < minGap) continue;
    const delta = signedHeadingChange(track, i);
    out.push({ index: i, sign: delta > 0 ? 1 : -1 });
    last = i;
  }
  return out;
}

function curvatureAt(track: Track, i: number): number {
  const n = track.centerline.length;
  const a = track.tangents[(i - 2 + n) % n];
  const b = track.tangents[(i + 2) % n];
  const dot = THREE.MathUtils.clamp(a.tx * b.tx + a.tz * b.tz, -1, 1);
  return Math.min(1, Math.acos(dot) * 4);
}

function signedHeadingChange(track: Track, i: number): number {
  const n = track.centerline.length;
  const a = track.tangents[(i - 4 + n) % n];
  const b = track.tangents[(i + 4) % n];
  return Math.atan2(a.tx * b.tz - a.tz * b.tx, a.tx * b.tx + a.tz * b.tz);
}

function makeChevronMaterial(): THREE.MeshBasicMaterial {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 96;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#171717';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#ffbb1d';
    ctx.lineWidth = 28;
    ctx.lineJoin = 'miter';
    // Arrows point in the direction of corner travel. Drawn with the apex on
    // the LEFT (x), opening to the right (x + 46). Combined with the holder's
    // Y rotation, this puts the arrow tips facing into the corner from the
    // racer's POV instead of marching the wrong way.
    for (let x = -40; x < 256; x += 78) {
      ctx.beginPath();
      ctx.moveTo(x + 46, 8);
      ctx.lineTo(x, 48);
      ctx.lineTo(x + 46, 88);
      ctx.stroke();
    }
    ctx.strokeStyle = 'rgba(255,255,255,0.24)';
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, 252, 92);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
}

function makeBannerMaterial(primary: string, secondary: string): THREE.MeshBasicMaterial {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const grd = ctx.createLinearGradient(0, 0, 0, 256);
    grd.addColorStop(0, primary);
    grd.addColorStop(1, secondary);
    ctx.fillStyle = grd;
    ctx.fillRect(0, 0, 128, 256);
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    ctx.beginPath();
    ctx.moveTo(64, 48);
    ctx.lineTo(88, 118);
    ctx.lineTo(64, 202);
    ctx.lineTo(40, 118);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = primary;
    ctx.fillRect(58, 72, 12, 102);
    ctx.fillRect(46, 114, 36, 10);
    ctx.fillStyle = '#111827';
    ctx.fillRect(0, 224, 128, 10);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return new THREE.MeshBasicMaterial({ map: texture, side: THREE.DoubleSide });
}

interface StaticInstance {
  matrix: THREE.Matrix4;
  centerlineIndex?: number;
  signedLateral?: number;
}

function composeInstanceMatrix(position: THREE.Vector3, yaw: number): THREE.Matrix4 {
  return new THREE.Matrix4().compose(
    position,
    new THREE.Quaternion().setFromAxisAngle(Y_AXIS, yaw),
    INSTANCE_SCALE,
  );
}

function composeChildInstanceMatrix(parentMatrix: THREE.Matrix4, position: THREE.Vector3, yaw: number): THREE.Matrix4 {
  return parentMatrix.clone().multiply(composeInstanceMatrix(position, yaw));
}

function makeStaticInstancedMesh(
  name: string,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  instances: StaticInstance[],
  options: { castShadow?: boolean; receiveShadow?: boolean } = {},
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geometry, material, instances.length);
  mesh.name = name;
  mesh.castShadow = options.castShadow ?? false;
  mesh.receiveShadow = options.receiveShadow ?? false;
  mesh.updateMatrix();
  mesh.matrixAutoUpdate = false;
  mesh.instanceMatrix.setUsage(THREE.StaticDrawUsage);
  for (let i = 0; i < instances.length; i++) {
    mesh.setMatrixAt(i, instances[i].matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  mesh.computeBoundingSphere();
  const first = instances[0];
  mesh.userData.instanceCount = instances.length;
  if (typeof first?.centerlineIndex === 'number') mesh.userData.centerlineIndex = first.centerlineIndex;
  if (typeof first?.signedLateral === 'number') mesh.userData.signedLateral = first.signedLateral;
  return mesh;
}

function disposeGroup(group: THREE.Group): void {
  const disposedGeometries = new Set<THREE.BufferGeometry>();
  const disposedMaterials = new Set<THREE.Material>();
  const disposedTextures = new Set<THREE.Texture>();
  for (const child of group.children) {
    child.traverse((object) => {
      if (object instanceof THREE.Mesh || object instanceof THREE.Line) {
        if (!disposedGeometries.has(object.geometry)) {
          disposedGeometries.add(object.geometry);
          object.geometry.dispose();
        }
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) {
          if (disposedMaterials.has(material)) continue;
          disposedMaterials.add(material);
          for (const value of Object.values(material)) {
            if (value instanceof THREE.Texture && !disposedTextures.has(value)) {
              disposedTextures.add(value);
              value.dispose();
            }
          }
          material.dispose();
        }
      }
    });
  }
  group.clear();
}
